import { useState } from 'react'
import { supabase } from '../../../lib/supabase'
import { useGeoAge } from '../../../hooks/useGeoAge'

// ── DOB Picker ────────────────────────────────────────────────────────────────
const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December']

function getDaysInMonth(month, year) {
  if (!month) return 31
  if (!year) return new Date(2000, parseInt(month), 0).getDate()
  return new Date(parseInt(year), parseInt(month), 0).getDate()
}

function DOBPicker({ value, onChange, inputClass, labelClass }) {
  const parts = value ? value.split('-') : ['', '', '']
  const [year, month, day] = parts
  const currentYear = new Date().getFullYear()
  const years = Array.from({ length: 100 }, (_, i) => currentYear - i)
  const daysInMonth = getDaysInMonth(month, year)
  const days = Array.from({ length: daysInMonth }, (_, i) => String(i + 1).padStart(2, '0'))

  const [localMonth, setLocalMonth] = useState(month || '')
  const [localDay, setLocalDay] = useState(day || '')
  const [localYear, setLocalYear] = useState(year || '')

  const notify = (m, d, y) => {
    if (m && d && y) {
      const maxDay = getDaysInMonth(m, y)
      const clampedDay = Math.min(parseInt(d), maxDay)
      onChange(`${y}-${m}-${String(clampedDay).padStart(2, '0')}`)
    } else onChange('')
  }

  const handleMonth = (m) => { setLocalMonth(m); notify(m, localDay, localYear) }
  const handleDay = (d) => { setLocalDay(d); notify(localMonth, d, localYear) }
  const handleYear = (y) => { setLocalYear(y); notify(localMonth, localDay, y) }

  return (
    <div className="flex flex-col gap-2">
      <label className={labelClass}>Date of Birth</label>
      <div className="grid grid-cols-3 gap-2">
        <select value={localMonth} onChange={e => handleMonth(e.target.value)} className={inputClass}>
          <option value="">Month</option>
          {MONTHS.map((m, i) => <option key={m} value={String(i + 1).padStart(2, '0')}>{m}</option>)}
        </select>
        <select value={localDay} onChange={e => handleDay(e.target.value)} className={inputClass}>
          <option value="">Day</option>
          {days.map(d => <option key={d} value={d}>{parseInt(d)}</option>)}
        </select>
        <select value={localYear} onChange={e => handleYear(e.target.value)} className={inputClass}>
          <option value="">Year</option>
          {years.map(y => <option key={y} value={y}>{y}</option>)}
        </select>
      </div>
    </div>
  )
}

// ── Phone row ─────────────────────────────────────────────────────────────────
function PhoneRow({ label, countryValue, numberValue, onCountryChange, onNumberChange, countries, inputClass, labelClass, helperText }) {
  return (
    <div className="flex flex-col gap-2">
      <label className={labelClass}>{label}</label>
      <div className="flex">
        <select
          value={countryValue}
          onChange={e => onCountryChange(e.target.value)}
          className="bg-gray-100 dark:bg-gray-900 border border-r-0 border-gray-200 dark:border-white/10 focus:border-green-600 text-gray-900 dark:text-white px-3 py-3 text-sm outline-none transition-colors w-28 flex-shrink-0"
        >
          <option value="">+</option>
          {countries.map(c => (
            <option key={c.country_code} value={c.country_code}>{c.dial_code}</option>
          ))}
        </select>
        <input
          type="tel"
          value={numberValue}
          onChange={e => onNumberChange(e.target.value)}
          placeholder="97 000 0000"
          className={`${inputClass} flex-1`}
        />
      </div>
      {helperText && (
        <p className="text-xs text-green-600 dark:text-green-400 flex items-center gap-1.5">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.09 9.81a19.79 19.79 0 01-3.07-8.63A2 2 0 012 .99h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.09 8.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/>
          </svg>
          {helperText}
        </p>
      )}
    </div>
  )
}

// ── Helper: split "+260 97 000 0000" into { country_code, number } ────────────
function splitPhone(fullPhone, countries) {
  if (!fullPhone) return { country_code: '', number: '' }
  for (const c of countries) {
    if (fullPhone.startsWith(c.dial_code + ' ')) {
      return { country_code: c.country_code, number: fullPhone.slice(c.dial_code.length + 1) }
    }
  }
  return { country_code: '', number: fullPhone }
}

// ── Main form ─────────────────────────────────────────────────────────────────
export default function EditProfileForm({ supporter, setSupporter }) {
  const { countries } = useGeoAge()

  const phoneInit = splitPhone(supporter?.phone, countries)
  const waInit = splitPhone(supporter?.whatsapp_number, countries)
  const nokPhoneInit = splitPhone(supporter?.next_of_kin_phone, countries)

  const [formData, setFormData] = useState({
    full_name: supporter?.full_name || '',
    gender: supporter?.gender || '',
    date_of_birth: supporter?.date_of_birth || '',
    region: supporter?.region || '',
  })

  const [phone, setPhone] = useState({ country_code: phoneInit.country_code, number: phoneInit.number })
  const [whatsapp, setWhatsapp] = useState({ country_code: waInit.country_code, number: waInit.number })
  const [nokName, setNokName] = useState(supporter?.next_of_kin_name || '')
  const [nokPhone, setNokPhone] = useState({ country_code: nokPhoneInit.country_code, number: nokPhoneInit.number })

  const [loading, setLoading] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState(null)
  const [photoUrl, setPhotoUrl] = useState(supporter?.profile_photo || null)

  const buildPhone = (country_code, number) => {
    const dial = countries.find(c => c.country_code === country_code)?.dial_code || ''
    return dial ? `${dial} ${number}`.trim() : number.trim()
  }

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value })

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0]
    if (!file) return
    if (!file.type.startsWith('image/')) { setError('Please upload an image file'); return }
    if (file.size > 2 * 1024 * 1024) { setError('Image must be under 2MB'); return }
    setUploading(true); setError(null)
    const fileExt = file.name.split('.').pop()
    const fileName = `${supporter.id}-photo.${fileExt}`
    const { error: uploadError } = await supabase.storage.from('profile-photos').upload(fileName, file, { upsert: true })
    if (uploadError) { setError(uploadError.message); setUploading(false); return }
    const { data: { publicUrl } } = supabase.storage.from('profile-photos').getPublicUrl(fileName)
    await supabase.from('supporters').update({ profile_photo: publicUrl }).eq('id', supporter.id)
    setPhotoUrl(publicUrl)
    setSupporter({ ...supporter, profile_photo: publicUrl })
    setUploading(false)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true); setError(null); setSuccess(false)
    const { error } = await supabase.from('supporters').update({
      ...formData,
      phone: buildPhone(phone.country_code, phone.number),
      whatsapp_number: buildPhone(whatsapp.country_code, whatsapp.number),
      next_of_kin_name: nokName,
      next_of_kin_phone: buildPhone(nokPhone.country_code, nokPhone.number),
    }).eq('id', supporter.id)
    if (error) setError(error.message)
    else { setSupporter({ ...supporter, ...formData }); setSuccess(true) }
    setLoading(false)
  }

  const initials = supporter?.full_name?.split(' ').map(n => n[0]).join('').toUpperCase()
  const inputClass = "bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-white/10 focus:border-green-600 text-gray-900 dark:text-white px-4 py-3 text-sm outline-none transition-colors placeholder:text-gray-400 dark:placeholder:text-gray-600 w-full"
  const labelClass = "text-xs text-gray-500 uppercase tracking-widest font-semibold"

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-6">

      {error && <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/50 text-red-600 dark:text-red-400 px-4 py-3 text-sm">{error}</div>}
      {success && <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800/50 text-green-600 dark:text-green-400 px-4 py-3 text-sm">Profile updated successfully.</div>}

      {/* ── Photo ── */}
      <div className="bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 p-5 flex items-center gap-5">
        <div className="w-16 h-16 rounded-full overflow-hidden bg-gray-200 dark:bg-white/10 border-2 border-gray-300 dark:border-white/20 flex items-center justify-center shrink-0">
          {photoUrl ? <img src={photoUrl} alt="Profile" className="w-full h-full object-cover" /> : <span className="text-gray-700 dark:text-white font-black text-xl">{initials}</span>}
        </div>
        <div className="flex flex-col gap-2 flex-1">
          <p className="text-xs text-gray-500 leading-relaxed">Clear headshot only — used for identity verification on your membership card.</p>
          <label className="bg-gray-200 dark:bg-white/10 hover:bg-gray-300 dark:hover:bg-white/20 text-gray-700 dark:text-white px-4 py-2 text-xs font-bold uppercase tracking-widest cursor-pointer transition-colors w-fit">
            {uploading ? 'Uploading...' : photoUrl ? 'Change Photo' : 'Upload Photo'}
            <input type="file" accept="image/*" onChange={handlePhotoUpload} disabled={uploading} className="hidden" />
          </label>
        </div>
      </div>

      {/* ── Personal ── */}
      <div className="bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 p-5 flex flex-col gap-4">
        <p className={labelClass}>Personal Details</p>
        <div className="flex flex-col gap-2">
          <label className={labelClass}>Full Name</label>
          <input type="text" name="full_name" value={formData.full_name} onChange={handleChange} className={inputClass} />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="flex flex-col gap-2">
            <label className={labelClass}>Gender</label>
            <select name="gender" value={formData.gender} onChange={handleChange} className={inputClass}>
              <option value="">Select</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div className="flex flex-col gap-2">
            <label className={labelClass}>Region / City</label>
            <input type="text" name="region" value={formData.region} onChange={handleChange} placeholder="Mufulira" className={inputClass} />
          </div>
        </div>
        <DOBPicker value={formData.date_of_birth} onChange={v => setFormData(p => ({ ...p, date_of_birth: v }))} inputClass={inputClass} labelClass={labelClass} />
      </div>

      {/* ── Contact ── */}
      <div className="bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 p-5 flex flex-col gap-4">
        <p className={labelClass}>Contact</p>
        <PhoneRow
          label="Phone Number" countries={countries}
          countryValue={phone.country_code} numberValue={phone.number}
          onCountryChange={v => setPhone(p => ({ ...p, country_code: v }))}
          onNumberChange={v => setPhone(p => ({ ...p, number: v }))}
          helperText="Must be available on WhatsApp"
          inputClass={inputClass} labelClass={labelClass}
        />
        <PhoneRow
          label="WhatsApp Number" countries={countries}
          countryValue={whatsapp.country_code} numberValue={whatsapp.number}
          onCountryChange={v => setWhatsapp(p => ({ ...p, country_code: v }))}
          onNumberChange={v => setWhatsapp(p => ({ ...p, number: v }))}
          inputClass={inputClass} labelClass={labelClass}
        />
      </div>

      {/* ── Next of Kin ── */}
      <div className="border-l-4 border-green-500 bg-green-50 dark:bg-green-900/10 p-5 flex flex-col gap-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-green-700 dark:text-green-400 mb-0.5">Emergency Contact</p>
          <p className="text-xs text-gray-500">Contacted in case of emergency or welfare situation.</p>
        </div>
        <div className="flex flex-col gap-2">
          <label className={labelClass}>Full Name</label>
          <input type="text" value={nokName} onChange={e => setNokName(e.target.value)} placeholder="Next of kin's full name" className={inputClass} />
        </div>
        <PhoneRow
          label="Phone Number" countries={countries}
          countryValue={nokPhone.country_code} numberValue={nokPhone.number}
          onCountryChange={v => setNokPhone(p => ({ ...p, country_code: v }))}
          onNumberChange={v => setNokPhone(p => ({ ...p, number: v }))}
          inputClass={inputClass} labelClass={labelClass}
        />
      </div>

      {/* ── Read-only ── */}
      <div className="grid grid-cols-2 gap-4">
        <div className="flex flex-col gap-2">
          <label className={labelClass}>Club</label>
          <div className="px-4 py-3 bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/5 text-gray-500 text-sm">{supporter?.clubs?.name}</div>
        </div>
        <div className="flex flex-col gap-2">
          <label className={labelClass}>Chapter</label>
          <div className="px-4 py-3 bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/5 text-gray-500 text-sm">{supporter?.chapters?.name}</div>
        </div>
      </div>

      <button type="submit" disabled={loading}
        className="bg-green-600 hover:bg-green-700 disabled:bg-green-900 disabled:text-green-700 text-white py-4 font-bold uppercase tracking-widest text-sm transition-colors">
        {loading ? 'Saving...' : 'Save Changes'}
      </button>
    </form>
  )
}